import { ZIndexRange } from '@/types/style'
import { Icon } from '@ur-solutions/react-components'
import { useClickOutside, useTranslate } from '@ur-solutions/react-hooks'
import {
  getISOWeek,
  getISOWeeksInYear,
  getISOWeekYear,
  isAfter,
  setISOWeek,
  startOfISOWeek,
} from 'date-fns'
import clamp from 'lodash/clamp'
import range from 'lodash/range'
import React, { useRef, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled, { css } from 'styled-components'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  user-select: none;
`

const Display = styled.div<{ open: boolean; light: boolean; height: string }>`
  display: grid;
  grid-template-columns: 2fr 140px;
  height: ${props => props.height};
  border: 1px solid ${props => props.theme.colors.gray9};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props =>
    props.light ? 'white' : props.theme.colors.quaternary};

  ${props =>
    props.open &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom-width: 0;

      .arrow {
        transform: translateY(-1px);
      }
    `};
`

const DisplayPart = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.4rem;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
`

const Curtain = styled.div<{ light: boolean }>`
  position: absolute;
  z-index: ${ZIndexRange.Dropdowns};
  top: 100%;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: 2fr 140px;
  background: ${props =>
    props.light ? 'white' : props.theme.colors.quaternary};
  box-shadow: 0 8px 8px -2px rgba(0, 0, 0, 0.2);
  border: solid ${props => props.theme.colors.gray9};
  border-width: 0 1px 1px 1px;
  border-bottom-left-radius: ${props => props.theme.sizes.defaultBorderRadius};
  border-bottom-right-radius: ${props => props.theme.sizes.defaultBorderRadius};
  max-height: 200px;
  overflow-y: auto;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 0.5rem;

  &:last-of-type {
    border-left: 1px solid ${props => props.theme.colors.gray9};
  }
`

const Item = styled.div<{ active: boolean; disabled?: boolean }>`
  position: relative;
  padding: 0.5rem 1.4rem;
  font-weight: 500;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};

  ${props =>
    props.active &&
    css`
      color: ${props.theme.colors.secondary};

      &::before {
        content: '';
        position: absolute;
        left: 0.4rem;
        transform: translateY(4px);
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${props.theme.colors.secondary};
      }
    `};
`

export const WeekPicker: React.FC<{
  value: Date
  futureYears?: number
  earliestYear?: number
  disableAfter?: boolean
  onChange: (date: Date) => void
  light?: boolean
  height?: string
}> = ({
  value,
  futureYears = 2,
  earliestYear = 2017,
  disableAfter = true,
  light = false,
  height = '3.875rem',
  onChange,
}) => {
  const translations = useTranslate({
    week: 'common.week',
  })

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const isoYear = getISOWeekYear(value)
  const selectedWeek = getISOWeek(value)
  const weeksInYear = getISOWeeksInYear(value)

  const handleWeekClick = (week: number) => {
    const newDate = setISOWeek(new Date(isoYear, 0, 4), week)
    onChange(newDate)
  }

  const years = (() => {
    const forwardYears = clamp(futureYears, 0, 10)
    return range(earliestYear, isoYear + forwardYears + 1)
  })()

  function handleYearClick(year: number) {
    const firstWeekDate = setISOWeek(new Date(year, 0, 4), 1)
    onChange(firstWeekDate)
  }

  useClickOutside(wrapperRef, () => setOpen(false), true)

  return (
    <Wrapper ref={wrapperRef}>
      <Display
        open={open}
        light={light}
        height={height}
        onClick={() => setOpen(v => !v)}
      >
        <DisplayPart>
          <span> {`${translations.week} ${getISOWeek(value)}`}</span>
          {!isMobileOnly && (
            <Icon icon="chevron-down" color="gray7" size="1rem" />
          )}
        </DisplayPart>
        <DisplayPart>
          <span>{isoYear}</span>
          <Icon icon="chevron-down" color="gray7" size="1rem" />
        </DisplayPart>
      </Display>

      {open && (
        <Curtain light={light}>
          <List>
            {range(1, weeksInYear + 1).map(week => {
              const weekDate = setISOWeek(new Date(isoYear, 0, 4), week)
              const disabled =
                disableAfter && isAfter(startOfISOWeek(weekDate), new Date())

              return (
                <Item
                  key={week}
                  active={week === selectedWeek}
                  onClick={() => !disabled && handleWeekClick(week)}
                  disabled={disabled}
                >
                  {`${translations.week} ${week}`}
                </Item>
              )
            })}
          </List>

          <List>
            {years.map(year => {
              const disabled = disableAfter && year > getISOWeekYear(new Date())

              return (
                <Item
                  key={year}
                  active={year === isoYear}
                  onClick={() => !disabled && handleYearClick(year)}
                  disabled={disabled}
                >
                  {year}
                </Item>
              )
            })}
          </List>
        </Curtain>
      )}
    </Wrapper>
  )
}
